import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import _debounce from 'lodash/debounce';

import { qaBackendApiInstance } from '../../config/config';

const mapOrientation = (orientation: string): string => {
  if (orientation === 'vertical') return 'portrait';
  if (orientation === 'horizontal') return 'landscape';
  return 'square';
};

export const fetchGettyVideos = async (
  query: string,
  orientation: string,
  page_size: number,
  page: number,
  image_techniques: string[] = []
) => {
  const orientations = mapOrientation(orientation);
  const { data } = await qaBackendApiInstance.post('/stock-media/search-videos', {
    searchQuery: query,
    page,
    orientations,
    page_size,
  });
  void image_techniques;
  return data;
};

export const useGetGettyImageVideos = (
  query = '',
  initialAspectRatio: string,
  orientation: string,
  page_size: number,
  initialPage: number,
  image_techniques: string[] = [],
  debounceDelay = 500
) => {
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio);
  const [page, setPage] = useState(initialPage);
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const queryClient = useQueryClient();

  const updateQuery = _debounce((newQuery: string) => {
    setDebouncedQuery(newQuery);
  }, debounceDelay);

  useEffect(() => {
    updateQuery(query);
  }, [query]);

  useEffect(() => {
    setPage(initialPage);
  }, [query, orientation, initialPage]);

  const result = useQuery(
    [
      'fetchGettyImageVideos',
      debouncedQuery,
      aspectRatio,
      orientation,
      page_size,
      page,
      image_techniques,
    ],
    () =>
      fetchGettyVideos(
        debouncedQuery,
        orientation,
        page_size,
        page,
        image_techniques
      ),
    {
      enabled: !!debouncedQuery,
      staleTime: 1000 * 60 * 5,
      onSuccess: (data: { videos?: unknown[] }) => {
        if (
          data?.videos?.length === 0 &&
          aspectRatio !== '16:9'
        ) {
          setAspectRatio('16:9');
          queryClient.invalidateQueries([
            'fetchGettyImageVideos',
            debouncedQuery,
            '16:9',
            orientation,
          ]);
        }
      },
    }
  );

  return {
    data: result.data,
    error: result.error,
    isLoading: result.isLoading,
    isSuccess: result.isSuccess,
    page,
    setPage,
  };
};
